// VerifyPhoneNumberModal.tsx
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { api } from "~/utils/api";
import { Label } from "../v3/shadcn/Label";
import { Button } from "../v3/themed/Button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../v3/shadcn/InputOTP";
import { useSession } from "next-auth/react";
import Modal from "../v3/themed/Modal";

type AddPhoneNumberModalProps = {
    setShowModal: (show: boolean) => void;
    refetchCallback?: () => void;
    verifiedCallback?: () => void;
};

const VerifyPhoneNumberModal = (props: AddPhoneNumberModalProps) => {
    const { data: session, update: updateSession } = useSession();

    const [verificationCode, setVerificationCode] = useState<string>("");
    const [error, setError] = useState<string>("");
    const modalRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                modalRef.current &&
                !modalRef.current.contains(e.target as Node)
            ) {
                props.setShowModal(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const verifyPhoneNumber = api.user.verifyPhoneNumber.useMutation();
    const handleVerifyPhoneNumber = async () => {
        await verifyPhoneNumber.mutateAsync(
            {
                verificationCode: verificationCode,
            },
            {
                onSuccess: async (data) => {
                    if (!data) {
                        setError(
                            "Something went wrong... Please try again later.",
                        );
                        return;
                    }

                    await updateSession({
                        ...session,
                        user: {
                            ...session?.user,
                            isUserPhoneNumberVerified: true,
                        },
                    });

                    props.setShowModal(false);
                    if (props.refetchCallback) props.refetchCallback();
                    if (props.verifiedCallback) {
                        toast.success("Phone number verified!");
                        props.verifiedCallback();
                    }
                },
            },
        );
    };

    return (
        <Modal open={true} onOpenChange={() => props.setShowModal(false)}>
            <div className="flex max-w-md flex-col justify-start overflow-x-hidden text-start">
                <Label>Verification code</Label>
                <div className="flex flex-row">
                    <div className="flex w-full flex-col">
                        <InputOTP
                            value={verificationCode}
                            onChange={setVerificationCode}
                            maxLength={6}
                        >
                            <InputOTPGroup>
                                {Array.from({ length: 6 }).map((_, index) => (
                                    <InputOTPSlot key={index} index={index} />
                                ))}
                            </InputOTPGroup>
                        </InputOTP>
                        {error && <p className="text-red-500">{error}</p>}
                    </div>
                </div>
            </div>
            <div>
                <Button className="w-[33%]" onClick={handleVerifyPhoneNumber}>
                    Confirm
                </Button>
                <Button
                    className="ml-4"
                    variant="secondary"
                    onClick={() => {
                        props.setShowModal(false);
                    }}
                >
                    Go back
                </Button>
            </div>
        </Modal>
    );
};

export default VerifyPhoneNumberModal;
